import { useEffect, useCallback, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import useStockDataModel from './stockDataModel';
import StockChart from '../StocksChart/stockChart';
import Navigation from '../components/Navigation/Navigation';
import UpstoxTokenStatus from '../components/UpstoxTokenStatus';
import './stockData.css';

export default function StockData() {
    const model = useStockDataModel();
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const searchInputRef = useRef(null);
    const suggestionsRef = useRef(null);

    // Destructure the needed function from model to avoid object dependency
    const { searchStocks } = model;
    
    // Memoize the search handler to prevent unnecessary re-renders
    const handleSearch = useCallback((query) => {
        if (query) {
            searchStocks(query);
            setSelectedIndex(-1); // Reset selection when search changes
        }
    }, [searchStocks]);

    useEffect(() => {
        // Don't search if the query matches the selected stock's symbol
        if (model.state.searchQuery && model.state.searchQuery !== model.state.selectedStock?.symbol) {
            const debounceTimer = setTimeout(() => {
                handleSearch(model.state.searchQuery);
            }, 300); // Debounce search requests

            return () => clearTimeout(debounceTimer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model.state.searchQuery, handleSearch, model.state.selectedStock]);

    useEffect(() => {
        if (model.state.symbol) {
            model.getStockPrice();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model.state.symbol, model.getStockPrice]);

    const handleStockSelect = (suggestion) => {
        model.updateState({
            symbol: suggestion.symbol,
            selectedStock: suggestion,
            suggestions: [], // Clear suggestions
            searchQuery: suggestion.symbol, // Update search query with selected symbol
            price: null, // Clear the previous price
            data: null  // Clear previous data
        });
        
        // Remove focus from input
        searchInputRef.current?.blur();
    };

    // Highlight matching text in suggestions
    const highlightMatch = (text, query) => {
        if (!query) return text;
        const parts = text.split(new RegExp(`(${query})`, 'gi'));
        return parts.map((part, i) => 
            part.toLowerCase() === query.toLowerCase() ? 
                <span key={i} className="bg-blue-500/20 text-blue-400">{part}</span> : part
        );
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!model.state.suggestions.length) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < model.state.suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0) {
                    handleStockSelect(model.state.suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                model.updateState({ suggestions: [] });
                setSelectedIndex(-1);
                break;
            default:
                break;
        }
    };



    // Loading skeleton component
    const Skeleton = ({ className }) => (
        <motion.div
            className={`animate-pulse bg-gray-700/50 rounded ${className}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        />
    );

    return (
        <div className="min-h-screen bg-[#0B1121] text-gray-100">
            <Navigation />
            <div className="p-4 md:p-8">
                <UpstoxTokenStatus />
                <motion.div
                className="max-w-5xl mx-auto space-y-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {!model.state.selectedStock && (
                    <div className="text-center mb-12">
                        <img src="/My_Logo_2.png" alt="StockAdi Logo" className="h-24 w-24 rounded-full object-cover mx-auto mb-4" />
                    </div>
                )}
                <div className="text-center mb-12">
                    <motion.h1 
                        className="text-5xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200 }}
                    >
                        {model.state.name}
                    </motion.h1>
                </div>
                
                <div className="relative space-y-6">
                    <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"/>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <input
                                ref={searchInputRef}
                                className="w-full bg-gray-800/40 text-gray-100 rounded-2xl pl-12 pr-6 py-4 outline-none border border-gray-700/50 focus:border-blue-500/50 transition-all shadow-lg backdrop-blur-xl placeholder:text-gray-500"
                                type="text"
                                value={model.state.searchQuery}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    if (value !== model.state.selectedStock?.symbol) {
                                        // Only update if the value is different from current selection
                                        model.updateState({ 
                                            searchQuery: value,
                                            symbol: '',
                                            selectedStock: null,
                                            suggestions: value.trim() === '' ? [] : model.state.suggestions // Clear suggestions if empty
                                        });
                                    }
                                }}
                                onFocus={(e) => {
                                    // Select all text when focusing
                                    e.target.select();
                                }}
                                onKeyDown={handleKeyDown}
                                placeholder="Search stocks by symbol or company name..."
                            />
                            <AnimatePresence>
                                {model.state.suggestions.length > 0 && (
                                    <motion.ul 
                                        ref={suggestionsRef}
                                        className="absolute w-full mt-2 bg-gray-800/90 backdrop-blur-xl rounded-2xl overflow-hidden shadow-2xl border border-gray-700/50 divide-y divide-gray-700/30 max-h-96 overflow-y-auto z-50"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                    >
                                        {model.state.suggestions.map((suggestion, index) => (
                                            <motion.li
                                                key={suggestion.symbol}
                                                onClick={() => handleStockSelect(suggestion)}
                                                className={`px-6 py-4 cursor-pointer transition-all ${
                                                    index === selectedIndex 
                                                        ? 'bg-blue-500/20 border-l-4 border-blue-500' 
                                                        : 'hover:bg-gray-700/30'
                                                }`}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                            >
                                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                                                    <span className="font-semibold text-lg">
                                                        {highlightMatch(suggestion.symbol, model.state.searchQuery)}
                                                    </span>
                                                    <span className="text-gray-400 text-sm">
                                                        {highlightMatch(suggestion.name, model.state.searchQuery)}
                                                    </span>
                                                </div>
                                            </motion.li>
                                        ))}
                                    </motion.ul>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>

                <AnimatePresence>
                    {model.state.selectedStock && (
                        <motion.div 
                            className="space-y-6"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <motion.div 
                                className="bg-gray-800/40 rounded-2xl p-8 backdrop-blur-xl border border-gray-700/50 shadow-lg"
                                initial={{ scale: 0.95 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 200 }}
                            >
                                <div className="text-center mb-4">
                                    <h3 className="text-2xl font-bold text-gray-100 mb-1">
                                        {model.state.selectedStock.name}
                                    </h3>
                                    <p className="text-gray-400 text-sm">
                                        {model.state.selectedStock.symbol}
                                    </p>
                                </div>
                                
                                {model.state.isLoading ? (
                                    <div className="space-y-4">
                                        <Skeleton className="h-8 w-2/3 mx-auto" />
                                        <Skeleton className="h-6 w-1/2 mx-auto" />
                                    </div>
                                ) : (
                                    model.state.price && (
                                        <motion.div 
                                            className="text-center"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.2 }}
                                        >
                                            <div className="text-4xl font-bold text-blue-400 mb-2">
                                                ₹{model.state.price.toFixed(2)}
                                            </div>
                                            <div className="text-sm text-gray-400">
                                                Last Updated: {new Date().toLocaleTimeString()}
                                            </div>
                                        </motion.div>
                                    )
                                )}
                            </motion.div>

                            <motion.div 
                                className="bg-gray-800/40 rounded-2xl overflow-hidden backdrop-blur-xl border border-gray-700/50 shadow-lg"
                                initial={{ scale: 0.95 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                            >
                                <div className="p-8">
                                    <StockChart symbol={model.state.symbol} name={model.state.selectedStock.name} />
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
        </div>
    );
}